import { api } from '../../lib/api.ts';

export interface CompressFailure {
  code: string;
}

export interface CompressOutcome {
  /** true when the server actually produced a smaller file. */
  compressed: boolean;
  originalSize: number;
  compressedSize: number;
}

function triggerDownload(blob: Blob, filename: string): void {
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename.toLowerCase().endsWith('.pdf') ? filename : `${filename}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
}

/** Reads an axios error whose body is a Blob (responseType 'blob') back into JSON. */
async function readBlobError(data: unknown): Promise<{ error?: string } | null> {
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text()) as { error?: string };
    } catch {
      return null;
    }
  }
  return null;
}

export const compressApi = {
  /**
   * Compress a single PDF on the server and trigger a browser download of the
   * result. Returns the before/after sizes so the UI can report the saving.
   * Throws a CompressFailure with the server's error code on 4xx.
   */
  compress: async (file: File): Promise<CompressOutcome> => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await api.post<Blob>('/compress', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        responseType: 'blob',
      });
      const base = file.name.replace(/\.pdf$/i, '') || 'document';
      triggerDownload(r.data, `${base}-compressed.pdf`);
      const originalSize = Number(r.headers['x-original-size']) || file.size;
      const compressedSize = Number(r.headers['x-compressed-size']) || r.data.size;
      return {
        compressed: String(r.headers['x-compressed']) === 'true',
        originalSize,
        compressedSize,
      };
    } catch (err) {
      const resp = (err as { response?: { data?: unknown } }).response;
      const parsed = await readBlobError(resp?.data);
      const failure: CompressFailure = { code: parsed?.error ?? 'compress_failed' };
      throw failure;
    }
  },
};
